import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { admin, type LlmEdge, type LlmProvider, type LlmStatus } from './admin-api';

// Connect-AI admin section (Integrations area). Pick a provider, set a write-only key + model, test
// the connection, and toggle each language edge. The key value is NEVER rendered -- only "configured"
// + the last four. With nothing connected, every edge shows Off and the app runs its deterministic
// floor unchanged.

const PROVIDERS: { id: LlmProvider; label: string; defaultModel: string }[] = [
  { id: 'openai', label: 'OpenAI', defaultModel: 'gpt-4o-mini' },
  { id: 'anthropic', label: 'Anthropic', defaultModel: 'claude-3-5-haiku-latest' },
  { id: 'gemini', label: 'Gemini', defaultModel: 'gemini-flash-latest' },
];

const EDGES: { id: LlmEdge; label: string; on: string; off: string; governance: string }[] = [
  { id: 'ask', label: 'Ask the system', on: 'A vague question is interpreted by the model when no keyword matches.', off: 'Deterministic keyword matching only; a vague question declines.', governance: 'Turning this on sends the question text to the configured provider.' },
  { id: 'explain', label: 'Explain this', on: 'Plain-language explanations are written by the model, within the guardrails.', off: 'The deterministic plain-language floor only.', governance: 'Turning this on sends the on-page facts to the configured provider.' },
  { id: 'ingest', label: 'Text ingestion', on: 'Pasted or fetched text is read into signal candidates by the model.', off: 'Numeric injection on the registry only; text ingestion is absent.', governance: 'Turning this on sends the source text to the configured provider.' },
];

export default function ConnectAi() {
  const qc = useQueryClient();
  const status = useQuery({ queryKey: ['admin', 'llm', 'status'], queryFn: admin.llmStatus });
  const [provider, setProvider] = useState<LlmProvider>('openai');
  const [key, setKey] = useState('');
  const [model, setModel] = useState('');
  const [testLine, setTestLine] = useState<string | null>(null);
  const [saveLine, setSaveLine] = useState<string | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'llm', 'status'] });

  const save = useMutation({
    mutationFn: () => admin.llmSet(provider, key.trim() || null, model.trim() || null),
    onSuccess: () => { setKey(''); setTestLine(null); setSaveLine(null); invalidate(); },
    // Surface the backend's 422 detail (e.g. "A new key is required when changing providers.") inline;
    // the api helper puts that detail in the Error message after the status code.
    onError: (e) => setSaveLine(e instanceof Error ? e.message.replace(/^\d{3}\s+/, '') : 'Could not save the connection.'),
  });
  const test = useMutation({
    mutationFn: admin.llmTest,
    onSuccess: (r) => { setTestLine(r.ok ? `Connected (${r.provider ?? 'provider'}). ${r.latency_ms ?? 0} ms.` : `Failed (${r.provider ?? 'provider'}). ${r.error ?? 'no response'}`); invalidate(); },
    onError: (e) => setTestLine(`Failed. ${e instanceof Error ? e.message : 'error'}`),
  });
  const clearKey = useMutation({ mutationFn: admin.llmClearKey, onSuccess: () => { setTestLine(null); invalidate(); } });
  const toggle = useMutation({
    mutationFn: (v: { edge: LlmEdge; enabled: boolean }) => admin.llmToggle(v.edge, v.enabled),
    onSuccess: invalidate,
  });

  const s: LlmStatus | undefined = status.data;
  const def = PROVIDERS.find((p) => p.id === provider)!.defaultModel;  // form dropdown default (the input placeholder)
  // The status row's default follows the SAVED provider, not the form dropdown -- so a saved Gemini
  // config with a blank model shows the Gemini default, never gpt-4o-mini.
  const savedDef = PROVIDERS.find((p) => p.id === s?.provider)?.defaultModel ?? def;

  // Initialize the form from the SAVED config once it loads (Model from the raw model_set, NOT the
  // resolved default), so an unset model doesn't read as a pending edit and an unedited form is clean.
  const initialized = useRef(false);
  useEffect(() => {
    if (s && !initialized.current) {
      initialized.current = true;
      if (s.provider) setProvider(s.provider as LlmProvider);
      setModel(s.model_set ?? '');
    }
  }, [s]);

  // Dirty when the form diverges from the saved config. Test validates the SAVED connection, so you
  // can only Test what you've saved (configure -> Save -> Test); the green light never lies.
  const dirty = !!s && (
    provider !== s.provider ||
    key.trim() !== '' ||
    (model.trim() || null) !== (s.model_set ?? null)
  );

  return (
    <div className="panel" style={{ marginTop: 14 }}>
      <div className="qhead">
        <span className="qdot" style={{ background: s?.configured ? 'var(--olive)' : 'var(--faint)' }} />
        Connect AI provider
      </div>
      <p className="small faint" style={{ marginBottom: 12 }}>
        Connect a language model so the three language edges can run their generative path. The key is
        encrypted and never shown. Each edge stays off until you turn it on; with no model connected,
        every edge runs its deterministic version and the app is unaffected.
      </p>

      {s && (
        <>
          <Row k="Provider" v={s.provider ?? 'not connected'} accent={s.configured ? 'olive' : undefined} />
          <Row k="Model" v={s.model ?? `${savedDef} (default)`} mono />
          <Row k="API key" v={s.configured ? `configured, ${s.key_source}, ending ${s.key_last4 ?? 'hidden'}` : 'not set'} />
          <Row
            k="Last test"
            v={s.last_test.at
              ? `${s.last_test.ok ? 'ok' : 'failed'}, ${new Date(s.last_test.at).toLocaleString()}${s.last_test.latency_ms != null ? `, ${s.last_test.latency_ms} ms` : ''}`
              : 'never tested'}
          />
        </>
      )}

      <div className="divider" />
      <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <Field label="Provider">
          <select
            className="c2-input"
            value={provider}
            onChange={(e) => { setProvider(e.target.value as LlmProvider); setModel(''); setTestLine(null); setSaveLine(null); }}
            aria-label="Provider"
            style={{ minWidth: 130 }}
          >
            {PROVIDERS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </Field>
        <Field label="API key (write-only)">
          <input
            className="c2-input"
            type="password"
            value={key}
            onChange={(e) => { setKey(e.target.value); setTestLine(null); setSaveLine(null); }}
            placeholder={s?.configured ? 'configured (blank keeps it)' : 'paste the key'}
            aria-label="API key"
            autoComplete="off"
            style={{ minWidth: 220 }}
          />
        </Field>
        <Field label="Model">
          <input className="c2-input" value={model} onChange={(e) => { setModel(e.target.value); setTestLine(null); setSaveLine(null); }} placeholder={def} aria-label="Model" style={{ minWidth: 180 }} />
        </Field>
      </div>
      <div className="row" style={{ gap: 8, marginTop: 12 }}>
        <button type="button" className="btn primary" onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="btn ghost" onClick={() => { setTestLine(null); test.mutate(); }} disabled={test.isPending || !s?.configured || dirty}>
          {test.isPending ? 'Testing…' : 'Test connection'}
        </button>
        {dirty && <span className="small faint" style={{ alignSelf: 'center' }}>Save to test</span>}
        {s?.configured && s.key_source === 'admin' && (
          <button type="button" className="btn ghost" onClick={() => clearKey.mutate()} disabled={clearKey.isPending}>Clear key</button>
        )}
      </div>
      {saveLine && <div className="small" style={{ marginTop: 8, color: 'var(--red)' }}>{saveLine}</div>}
      {testLine && <div className="small" style={{ marginTop: 10, color: 'var(--ink)' }}>{testLine}</div>}

      <div className="divider" />
      <div className="small faint" style={{ marginBottom: 8 }}>
        Language edges{!s?.configured && ' (connect and save a model to enable)'}
      </div>
      {EDGES.map((e) => {
        const on = !!s?.edges[e.id];
        return (
          <div key={e.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--line-soft)' }}>
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, fontWeight: 600 }}>{e.label}</span>
              <button
                type="button"
                className={`btn ${on ? 'primary' : 'ghost'}`}
                style={{ minHeight: 'auto', padding: '4px 12px', fontSize: 12 }}
                onClick={() => toggle.mutate({ edge: e.id, enabled: !on })}
                disabled={toggle.isPending || !s?.configured}
                aria-label={`${e.label}: ${on ? 'on' : 'off'}`}
              >
                {on ? 'On' : 'Off'}
              </button>
            </div>
            <div className="small faint" style={{ marginTop: 4 }}>{on ? e.on : e.off}</div>
            <div className="small faint" style={{ marginTop: 2, fontStyle: 'italic' }}>{e.governance}</div>
          </div>
        );
      })}
    </div>
  );
}

function Row({ k, v, mono, accent }: { k: string; v: string; mono?: boolean; accent?: 'olive' }) {
  return (
    <div className="row" style={{ justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #241f18' }}>
      <span style={{ fontSize: 14 }}>{k}</span>
      {accent ? <span className={`tag ${accent}`} style={{ fontSize: 12 }}>{v}</span> : <span className={mono ? 'mono small' : 'small'}>{v}</span>}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <label className="small faint" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  );
}
